import { type ContentNode, ContentNodeUtils } from '@helo/cms'
import { Assert, Value, flow, pipe } from '@helo/utils'
import { getTicks, scaleLinear } from '@visx/scale'

import { Options } from '../../Options'
import type { QueryOutput } from '../../Query'
import {
  RenderPipeline,
  type RenderPipelineContext,
} from '../../RenderPipeline'
import type { ChartDataPoint } from '../ChartData'
import type { ResolvedChartSeries } from '../ChartSeries'
import { ChartCartesianConstants } from './ChartCartesian.constants'
import {
  type ChartCartesianOptions,
  chartCartesianOptions,
} from './ChartCartesian.options'

type CartesianInputCtx = RenderPipelineContext<
  ChartCartesianOptions & { _yAxis?: ChartCartesianOptions },
  Pick<QueryOutput, 'contentNode'>
>

/** Chart option state stored on the list content node. */
const getListContentNodeState = () =>
  flow(
    (ctx: { output: { contentNode?: ContentNode | null } }) =>
      ctx.output.contentNode,
    Assert.orThrow(Value.isNotNullable),
    Assert.orThrow(ContentNodeUtils.isKind('FIELD')),
    ContentNodeUtils.getOfType(),
    Assert.orThrow(ContentNodeUtils.isKind('LIST')),
    ContentNodeUtils.getState(),
  )

/**
 * Nested `_yAxis` bag on that content-node state.

 * Saved settings for the number axis: min, max, and how many tick marks.
 */
const getYAxisState = () => (state: Record<string, unknown>) => state._yAxis

/**
 * Resolve root input: override, then content-node state, then defaults.

 * The chart's min, max, and tick-count after filling in saved values.
 */
const setupInput = () =>
  RenderPipeline.updateInput((input: CartesianInputCtx['input'], ctx) =>
    pipe(
      input,
      Options.fallback(chartCartesianOptions, getListContentNodeState()(ctx)),
      Options.applyDefaults(chartCartesianOptions),
      Options.validate(chartCartesianOptions),
    ),
  )

/**
 * Same resolution for `_yAxis`, using nested `_yAxis` state.

 * The number axis's own min, max, and tick-count when those are set separately.
 */
const setup_yAxisInput = () =>
  RenderPipeline.updateInput((input, ctx) => ({
    ...input,
    _yAxis: pipe(
      input._yAxis ?? {},
      Options.fallback(
        chartCartesianOptions,
        getYAxisState()(
          getListContentNodeState()(ctx) as Record<string, unknown>,
        ) as object,
      ),
      Options.applyDefaults(chartCartesianOptions),
      Options.validate(chartCartesianOptions),
    ),
  }))

/**
 * Nice a numeric domain and return matching ticks.

 * Round the value axis to clean start/end numbers and list the labels on it.
 */
const resolveNiceYAxis = (
  min: number,
  max: number,
  tickCount: number = ChartCartesianConstants.yAxisTickCount,
): { yDomain: [number, number]; yTicks: number[] } => {
  const scale = scaleLinear<number>({
    domain: [min, max],
    nice: tickCount,
  })
  const [niceMin, niceMax] = scale.domain()
  return {
    yDomain: [niceMin, niceMax],
    yTicks: getTicks(scale, tickCount) as number[],
  }
}

/**
 * Pick axis min/max from data extent; floor at 0 unless values go negative.

 * How far the value axis should run. Starts at 0 unless some values are below it.
 */
const chooseMeasureAxisDomain = ({
  measureMin,
  measureMax,
  yAxisMin,
  yAxisMax,
}: {
  measureMin: number
  measureMax: number
  yAxisMin?: number
  yAxisMax?: number
}): { yMin: number; yMax: number } => {
  const autoMin = measureMin < 0 ? measureMin : 0
  let yMin = yAxisMin ?? autoMin
  let yMax = yAxisMax ?? Math.max(measureMax, yMin)
  if (yMin === yMax) yMax = yMin + 1
  return { yMin, yMax }
}

/**
 * Nice only the domain ends that are not forced by min/max options.

 * Snap unlocked axis ends to round numbers. Leave a locked min or max alone.
 */
const niceUnforcedEnds = ({
  yMin,
  yMax,
  minForced,
  maxForced,
  tickCount = ChartCartesianConstants.yAxisTickCount,
}: {
  yMin: number
  yMax: number
  minForced: boolean
  maxForced: boolean
  tickCount?: number
}): [number, number] => {
  const niced = resolveNiceYAxis(yMin, yMax, tickCount)
  return [
    minForced ? yMin : niced.yDomain[0],
    maxForced ? yMax : niced.yDomain[1],
  ]
}

/**
 * Ticks for an already-chosen domain.

 * The numbers drawn along the value axis.
 */
const ticksForDomain = (
  yDomain: [number, number],
  tickCount: number = ChartCartesianConstants.yAxisTickCount,
): number[] =>
  getTicks(scaleLinear<number>({ domain: yDomain }), tickCount) as number[]

/**
 * Choose, nice, and tick the measure axis in one pass.

 * The value axis range and the numbers labeled on it.
 */
const resolveYDomainAndTicks = ({
  measureMin,
  measureMax,
  yAxisMin,
  yAxisMax,
  tickCount = ChartCartesianConstants.yAxisTickCount,
}: {
  measureMin: number
  measureMax: number
  yAxisMin?: number
  yAxisMax?: number
  tickCount?: number
}): { yDomain: [number, number]; yTicks: number[] } => {
  const { yMin, yMax } = chooseMeasureAxisDomain({
    measureMin,
    measureMax,
    yAxisMin,
    yAxisMax,
  })
  const yDomain = niceUnforcedEnds({
    yMin,
    yMax,
    minForced: yAxisMin != null,
    maxForced: yAxisMax != null,
    tickCount,
  })
  return { yDomain, yTicks: ticksForDomain(yDomain, tickCount) }
}

/**
 * Category domain from series points, first-seen order.

 * The labels along the category axis, in the order they first appear.
 */
const buildXDomain = (series: ResolvedChartSeries[]): string[] => {
  const values: string[] = []
  for (const seriesItem of series) {
    for (const point of seriesItem.points) {
      if (!values.includes(point.x)) values.push(point.x)
    }
  }
  return values
}

/**
 * Category domain in source-row order.

 * The labels along the category axis, in the same order as the table rows.
 */
const buildXDomainFromRows = (
  rows: Record<string, unknown>[],
  dimensionKey: string,
): string[] =>
  rows.reduce<string[]>((values, row) => {
    const value = String(row[dimensionKey] ?? '')
    if (!values.includes(value)) values.push(value)
    return values
  }, [])

/**
 * Numeric min/max across series points; skips null and NaN.

 * The smallest and largest values in the data, so we know how tall the plot gets.
 */
const measureExtent = (
  series: ResolvedChartSeries[],
): { measureMin: number; measureMax: number } => {
  const measureValues = series.flatMap(seriesItem =>
    seriesItem.points.flatMap((point: ChartDataPoint) =>
      point.y == null || Number.isNaN(point.y) ? [] : [point.y],
    ),
  )
  return {
    measureMin: measureValues.length ? Math.min(...measureValues) : 0,
    measureMax: measureValues.length ? Math.max(...measureValues) : 0,
  }
}

type CartesianSetupCtx = {
  input: {
    _yAxis?: {
      min?: number
      max?: number
      tickCount?: number
    }
    min?: number
    max?: number
    tickCount?: number
  }
  output: {
    data?: { chartData?: Record<string, unknown>[]; dimensionKey: string }
    series?: ResolvedChartSeries[]
    cartesian?: {
      xDomain?: string[]
      measureMin?: number
      measureMax?: number
      yDomain?: [number, number]
      yTicks?: number[]
      plotOrientation?: 'vertical' | 'horizontal'
      categoryScale?: 'point' | 'band'
      min?: number
      max?: number
      tickCount?: number
    }
  }
}

/** Merge a cartesian slice onto `output.cartesian`. */
const mergeCartesian = (
  slice: (
    ctx: CartesianSetupCtx,
  ) => NonNullable<CartesianSetupCtx['output']['cartesian']>,
) =>
  RenderPipeline.updateOutput((output: CartesianSetupCtx['output'], ctx) => ({
    cartesian: {
      ...output.cartesian,
      ...slice(ctx),
    },
  }))

/**
 * Resolved min/max/tickCount; `_yAxis` overrides the root.

 * Whether the user locked the axis ends, and how many tick marks they asked for.
 */
const axisOptions = (ctx: CartesianSetupCtx) => {
  const yAxis = ctx.input._yAxis ?? {}
  return {
    min: yAxis.min ?? ctx.input.min,
    max: yAxis.max ?? ctx.input.max,
    tickCount:
      yAxis.tickCount ??
      ctx.input.tickCount ??
      ChartCartesianConstants.yAxisTickCount,
  }
}

/**
 * Seed `output.cartesian` with the resolved axis options.

 * Stores the user's axis min, max, and tick-count on the plot.
 */
const setCartesianModel = () => mergeCartesian(axisOptions)

/**
 * Write the category domain from query rows.

 * The labels along the bottom of a vertical chart, or the left of a horizontal one.
 */
const setCategoryDomainFromRows = () =>
  mergeCartesian(ctx => ({
    xDomain: buildXDomainFromRows(
      ctx.output.data?.chartData ?? [],
      ctx.output.data?.dimensionKey ?? '',
    ),
  }))

/**
 * Write measure min/max from series values.

 * How high and low the data goes, before the axis is rounded.
 */
const setMeasureExtent = () =>
  mergeCartesian(ctx => measureExtent(ctx.output.series ?? []))

/**
 * Write the raw measure-axis domain from extent and forced ends.

 * The start and end of the value axis, before rounding to even numbers.
 */
const setMeasureAxisDomain = () =>
  mergeCartesian(ctx => {
    const cartesian = ctx.output.cartesian ?? {}
    const { yMin, yMax } = chooseMeasureAxisDomain({
      measureMin: cartesian.measureMin ?? 0,
      measureMax: cartesian.measureMax ?? 0,
      yAxisMin: cartesian.min,
      yAxisMax: cartesian.max,
    })
    return { yDomain: [yMin, yMax] }
  })

/**
 * Nice the current y-domain ends that are not forced.

 * Round unlocked axis ends so tick labels look even. Locked ends stay put.
 */
const niceUnforcedMeasureEnds = () =>
  mergeCartesian(ctx => {
    const cartesian = ctx.output.cartesian ?? {}
    const [yMin, yMax] = cartesian.yDomain ?? [0, 1]
    return {
      yDomain: niceUnforcedEnds({
        yMin,
        yMax,
        minForced: cartesian.min != null,
        maxForced: cartesian.max != null,
        tickCount: cartesian.tickCount,
      }),
    }
  })

/**
 * Write ticks for the current y-domain.

 * The numbers labeled along the value axis.
 */
const setMeasureTicks = () =>
  mergeCartesian(ctx => {
    const cartesian = ctx.output.cartesian ?? {}
    return {
      yTicks: ticksForDomain(cartesian.yDomain ?? [0, 1], cartesian.tickCount),
    }
  })

/**
 * Default category layout: vertical plot, point scale.

 * Categories sit on a horizontal axis. Each one is a single position, not a bar slot.
 */
const setCategoryLayout = () =>
  mergeCartesian(() => ({
    plotOrientation: 'vertical',
    categoryScale: 'point',
  }))

export const ChartCartesianInputParts = {
  setupInput,
  setup_yAxisInput,
}

export const ChartCartesianOutputParts = {
  resolveNiceYAxis,
  chooseMeasureAxisDomain,
  niceUnforcedEnds,
  ticksForDomain,
  resolveYDomainAndTicks,
  buildXDomain,
  buildXDomainFromRows,
  measureExtent,
  setCartesianModel,
  setCategoryDomainFromRows,
  setMeasureExtent,
  setMeasureAxisDomain,
  niceUnforcedMeasureEnds,
  setMeasureTicks,
  setCategoryLayout,
}

export const ChartCartesianUtils = ChartCartesianOutputParts
